import { useCallback, useState, useRef } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import classNames from 'classnames';

import { length } from 'stringz';

import AutosuggestInput from 'mastodon/components/autosuggest_input';
import AutosuggestTextarea from 'mastodon/components/autosuggest_textarea';
import { Avatar } from 'mastodon/components/avatar';
import { Button } from 'mastodon/components/button';
import { DisplayName } from 'mastodon/components/display_name';
import { CharacterCounter } from 'mastodon/features/compose/components/character_counter';
import { EditIndicator } from 'mastodon/features/compose/components/edit_indicator';
import { PollForm } from 'mastodon/features/compose/components/poll_form';
import PrivacyDropdown from 'mastodon/features/compose/components/privacy_dropdown';
import { UploadForm } from 'mastodon/features/compose/components/upload_form';
import EmojiPickerDropdown from 'mastodon/features/compose/containers/emoji_picker_dropdown_container';
import WarningContainer from 'mastodon/features/compose/containers/warning_container';
import { me } from 'mastodon/initial_state';
import type { Status, StatusVisibility } from 'mastodon/models/status';
import { RootState, useAppSelector } from 'mastodon/store';
import { IconButton } from 'mastodon/components/icon_button';
import { closeModal } from 'mastodon/actions/modal';
import { useAppDispatch } from 'mastodon/store';
import { CloseIcon } from 'mastodon/components/icons/close';

const messages = defineMessages({
  cancel_reblog: { id: 'status.cancel_reblog_private', defaultMessage: 'Unboost' },
  reblog: { id: 'status.reblog', defaultMessage: 'Boost' },
  placeholder: { id: 'compose_form.placeholder', defaultMessage: 'What is on your mind?' },
  placeholderProfile: { id: 'compose_form.profile.placeholder', defaultMessage: 'How was your day today?' },
  spoiler_placeholder: { id: 'compose_form.spoiler_placeholder', defaultMessage: 'Content warning (optional)' },
});

export const BoostModal: React.FC<{
  status: Status;
  onClose: () => void;
  onReblog: (status: Status, privacy: StatusVisibility, caption?: string) => void;
}> = ({ status, onReblog, onClose }) => {
  const intl = useIntl();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const spoilerTextRef = useRef<any>(null);
  const dispatch = useAppDispatch();

  const defaultPrivacy = useAppSelector(
    (state) => state.compose.get('default_privacy') as StatusVisibility,
  );
  const statusVisibility = status.get('visibility') as StatusVisibility;

  const [privacy, setPrivacy] = useState<StatusVisibility>(
    statusVisibility === 'private' ? 'private' : defaultPrivacy,
  );
  const [caption, setCaption] = useState<string>('');
  const [spoilerText, setSpoilerText] = useState<string>('');

  const maxChars = 500;
  const spoiler = false;

  const currentUser = useAppSelector((state: RootState) =>
    me ? state.accounts.get(me) : undefined,
  );

  const handleReblog = useCallback(() => {
    if (length(caption) <= maxChars) {
      onReblog(status, privacy, caption);
      onClose();
    }
  }, [onClose, onReblog, status, privacy, caption]);

  const findContainer = useCallback(
    () => document.getElementsByClassName('modal-root__modal')[0],
    [],
  );

  const onPrivacyChange = useCallback((value: StatusVisibility) => {
    setPrivacy(value);
  }, []);

  const handleChange = useCallback((input: string | React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = typeof input === 'string' ? input : input.target.value;
    if (length(text) <= maxChars) {
      setCaption(text);
    }
  }, [maxChars]);

  const handleSpoilerChange = useCallback((text: string) => {
    setSpoilerText(text);
  }, []);

  const handleEmojiPick = useCallback((data: { id: string; native?: string; colons?: string; custom?: boolean }) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const position = textarea.selectionStart || 0;
    const emojiText = data.native ?? data.colons ?? `:${data.id}:`;
    const newCaption = caption.slice(0, position) + emojiText + caption.slice(position);

    if (length(newCaption) <= maxChars) {
      setCaption(newCaption);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(position + emojiText.length, position + emojiText.length);
      }, 0);
    }
  }, [caption, maxChars]);

  // Hàm rỗng cho AutosuggestTextarea và AutosuggestInput
  const onSuggestionsFetchRequested = useCallback(() => { }, []);
  const onSuggestionsClearRequested = useCallback(() => { }, []);
  const onSuggestionSelected = useCallback(() => { }, []);

  const handleClose = useCallback(() => {
      dispatch(closeModal({
        modalType: 'BOOST',
        ignoreFocus: false,
      }));
  }, [dispatch]);

  const getPlaceholder = () => {
    const isProfilePage = window.location.pathname.match(/^\/@[\w]+$/);
    return intl.formatMessage(isProfilePage ? messages.placeholderProfile : messages.placeholder);
  };

  return (
      <div className='modal-root__modal safety-action-modal'>
        <div className='compose-form-modal__header'>
        <IconButton
          className='compose-form-modal__close'
          title={''}
          label={''}
          icon='times'
          iconComponent={CloseIcon}
          onClick={handleClose}
        />
      </div>

        <div className='safety-action-modal__top'>
          <div className='safety-action-modal__header'>
            <div className='safety-action-modal__header__icon'>
              <Avatar account={currentUser} size={36} />
            </div>
            <div className='safety-action-modal__header__info'>
              <h1>
                <DisplayName account={currentUser} />
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {!status.get('reblogged') && (
                  <PrivacyDropdown
                    noDirect
                    value={privacy}
                    container={document.body}
                    onChange={onPrivacyChange}
                    disabled={statusVisibility === 'private'}
                  />
                )}
              </div>
            </div>
          </div>

          {!status.get('reblogged') && (
            <div className='boost-modal__compose compose-form-bottom-sheet__content'>
              <form className='compose-form'>
                <WarningContainer />
                <div className={classNames('compose-form__highlightable')}>
                  <div className='compose-form__scrollable'>
                    {/* <EditIndicator /> */}
                    {spoiler && (
                      <div className='spoiler-input'>
                        <div className='spoiler-input__border' />
                        <AutosuggestInput
                          placeholder={getPlaceholder()}
                          value={spoilerText}
                          onChange={handleSpoilerChange}
                          suggestions={[]}
                          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                          onSuggestionsClearRequested={onSuggestionsClearRequested}
                          onSuggestionSelected={onSuggestionSelected}
                          searchTokens={[':']}
                          id='cw-spoiler-input'
                          className='spoiler-input__input'
                          lang="en"
                          ref={spoilerTextRef}
                          spellCheck
                        />
                        <div className='spoiler-input__border' />
                      </div>
                    )}
                    <AutosuggestTextarea
                      ref={textareaRef}
                      placeholder={getPlaceholder()}
                      value={caption}
                      onChange={handleChange}
                      suggestions={[]}
                      onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                      onSuggestionsClearRequested={onSuggestionsClearRequested}
                      onSuggestionSelected={onSuggestionSelected}
                      autoFocus={false}
                      lang="en"
                    />
                  </div>
                  <UploadForm />
                  <PollForm />
                  <div className='compose-form__footer'>
                    <div className='compose-form__actions'>
                      <div className='compose-form__buttons'>
                        <CharacterCounter max={maxChars} text={caption} />
                        {window.innerWidth >= 768 && <EmojiPickerDropdown
                          isComposeForm
                          onPickEmoji={handleEmojiPick}
                          popperConfig={{
                            strategy: 'absolute',
                            modifiers: [
                              {
                                name: 'preventOverflow',
                                options: { boundary: findContainer() },
                              },
                              {
                                name: 'offset',
                                options: { offset: [0, 8] },
                              },
                            ],
                          }}
                        />}
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>

        <div className={classNames('safety-action-modal__bottom')}>
          <div className='safety-action-modal__actions'>
            <Button
              onClick={handleReblog}
              text={intl.formatMessage(status.get('reblogged') ? messages.cancel_reblog : messages.reblog)}
              autoFocus
              className='boost-button'
            />
          </div>
        </div>
      </div>
  );
};
